import {
  AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, OnInit, Signal, signal, viewChild, WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { ExperimentService } from '@services/experiment/experiment.service';
import { OnboardingProfileValidationService } from '@services/onboarding/onboarding-profile-validation.service';
import { UserService } from '@services/user';
import { GenderFormErrorMessages, GenderFormErrorMessagesType } from '@shared/constants';
import {
  ONBOARDING_CONCERN_QUERY_PARAMS, ONBOARDING_CONCERN_ROUTE, ONBOARDING_PROFILE_ROUTES,
} from '../onboarding-profile.constants';
import { OnboardingProfileFieldErrorComponent } from '../onboarding-profile-field-error/onboarding-profile-field-error.component';
import { OnboardingProfileShellComponent } from '../onboarding-profile-shell/onboarding-profile-shell.component';

interface TruecallerProfileName {
  family_name?: string;
  given_name?: string;
}

const MAX_SUGGESTED_NAME_WORDS = 3;

/** Step 2 of the split profile flow. Hands off to the age page, or class selection under `skip_age_page`. */
@Component({
  selector: 'app-onboarding-profile-name',
  templateUrl: './onboarding-profile-name.component.html',
  imports: [OnboardingProfileShellComponent, OnboardingProfileFieldErrorComponent, ButtonCtaComponent, FormsModule],
})
export class OnboardingProfileNameComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly router: Router = inject(Router);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly validation: OnboardingProfileValidationService = inject(OnboardingProfileValidationService);
  private readonly userService: UserService = inject(UserService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);

  private readonly nameInput: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild('nameInput');
  private nameEditedByUser = false;
  private truecallerSuggestion = '';

  protected readonly patientName: WritableSignal<string> = signal('');
  protected readonly isNameFromTruecaller: WritableSignal<boolean> = signal(false);
  protected readonly loading: WritableSignal<boolean> = signal(false);
  protected readonly errorMessage: WritableSignal<string> = signal('');
  protected readonly GenderFormErrorMessages: GenderFormErrorMessagesType = GenderFormErrorMessages;

  protected readonly hasError: Signal<boolean> = computed((): boolean => !!this.errorMessage());

  protected readonly isFilled: Signal<boolean> = computed((): boolean => this.patientName().length > 0);

  protected readonly isValidAndFilled: Signal<boolean> = computed(
    (): boolean => this.isFilled() && !this.hasError(),
  );

  protected readonly isDravidianScriptUser: Signal<boolean> = computed(
    (): boolean => this.userService.dravidianScripts.includes(this.conn.getActingUser()?.get('languagePreference')),
  );

  ngOnInit(): void {
    const savedName = this.conn.getActingUser()?.get('PatientName') || '';
    this.patientName.set(savedName);
    this.currentComponent.set(this);
    if (!savedName) void this.prefillNameFromTruecaller();
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
  }

  /** Autofocus so the keyboard is already up when the page lands. */
  ngAfterViewInit(): void {
    this.nameInput()?.nativeElement.focus();
  }

  protected onNameChange(value: string): void {
    this.nameEditedByUser = true;
    this.patientName.set(value);
    if (value !== this.truecallerSuggestion) this.isNameFromTruecaller.set(false);
    if (this.hasError()) this.errorMessage.set('');
  }

  protected clearPrefilledName(): void {
    this.nameEditedByUser = true;
    this.truecallerSuggestion = '';
    this.isNameFromTruecaller.set(false);
    this.patientName.set('');
    this.errorMessage.set('');
    this.nameInput()?.nativeElement.focus();
  }

  private async prefillNameFromTruecaller(): Promise<void> {
    try {
      const extraInformation: { truecallerProfile?: TruecallerProfileName } = await this.conn.getUserExtraInformation();
      const suggestedName = this.extractSuggestedNameFromTrueCaller(extraInformation?.truecallerProfile);
      if (!suggestedName || this.nameEditedByUser || this.patientName()) return;
      this.truecallerSuggestion = suggestedName;
      this.patientName.set(suggestedName);
      this.isNameFromTruecaller.set(true);
    } catch {}
  }

  private extractSuggestedNameFromTrueCaller(profile: TruecallerProfileName | undefined): string {
    const words = [profile?.given_name, profile?.family_name]
      .flatMap((part: string | undefined): string[] => part?.trim().split(/\s+/) ?? [])
      .filter((word: string): boolean => !!word)
      .slice(0, MAX_SUGGESTED_NAME_WORDS);
    const suggestedName = words.join(' ');
    if (!suggestedName || this.validation.validateName(suggestedName)) return '';
    return suggestedName;
  }

  /** Paste is blocked so the name cannot bypass the character rules. */
  protected onInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  protected async handleContinue(): Promise<void> {
    if (this.loading()) return;
    const error = this.validation.validateName(this.patientName());
    if (error) {
      this.errorMessage.set(error);
      this.nameInput()?.nativeElement.focus();
      return;
    }
    this.errorMessage.set('');
    this.loading.set(true);
    try {
      await this.conn.updateUserData({ PatientName: this.patientName() });
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: String(error) });
      return;
    } finally {
      this.loading.set(false);
    }
    await this.navigateNext();
  }

  private async navigateNext(): Promise<void> {
    if (this.experimentService.shouldSkipAgePage()) {
      await this.router.navigate([ONBOARDING_CONCERN_ROUTE], { queryParams: ONBOARDING_CONCERN_QUERY_PARAMS });
      return;
    }
    await this.router.navigate([ONBOARDING_PROFILE_ROUTES.AGE]);
  }

  async handleBackPress(): Promise<boolean> {
    return true;
  }
}
